import { useContext, useEffect, useState } from 'react';

import Button from '../../reusable/button/button.component';
import FulfillmentItemSelector from './fulfillment-item-selector/fulfillment-item-selector.component';
import Spinner from '../../reusable/spinner/spinner.component';

import { ToastContext } from '../../../contexts/toast.context';

import Client from '../../../tools/client';

import {
  ContentContainer,
  MainContainer,
  MainTitle,
  Subtitle,
  Input
} from '../../../styles/component.styles';

const client = new Client();

const AddFulfillment = ({ order, refreshOrder }) => {
  const [ loading, setLoading ] = useState(false);
  const [ quantities, setQuantities ] = useState({});
  const [ submitting, setSubmitting ] = useState(false);
  const [ orderItemsWithRemaining, setOrderItemsWithRemaining ] = useState([]);
  const [ trackingNumber, setTrackingNumber ] = useState('');
  const [ allFulfilled, setAllFulfilled ] = useState(false);
  
  const { successToast, errorToast } = useContext(ToastContext);

  useEffect(() => {
    if (!order || !order.OrderItems) return;

    const fulfillmentMap = {};

    // Build a map of fulfillment quantities by orderItemId
    for (const fulfillment of order.Fulfillments || []) {
      for (const fi of fulfillment.FulfillmentItems || []) {
        if (!fulfillmentMap[fi.orderItemId]) {
          fulfillmentMap[fi.orderItemId] = 0;
        }
        fulfillmentMap[fi.orderItemId] += fi.quantity;
      }
    }

    const items = order.OrderItems.map((item) => {
      const fulfilledQty = fulfillmentMap[item.id] || 0;
      return {
        orderItemId: item.id,
        productName: item.Product?.name || '—',
        sku: item.Product?.Inventories?.[0]?.sku || '—',
        size: item.Product?.Inventories?.[0]?.size || '—',
        total: item.quantity,
        fulfilled: fulfilledQty,
        remaining: Math.max(0, item.quantity - fulfilledQty)
      };
    });

    setOrderItemsWithRemaining(items);

    const defaultQuantities = {};
    let fullyFulfilled = true;

    for (const i of items) {
      defaultQuantities[i.orderItemId] = i.remaining;
      if (i.remaining > 0) fullyFulfilled = false;
    }

    setQuantities(defaultQuantities);
    setAllFulfilled(fullyFulfilled);
  }, [order]);

  const handleQuantityChange = (orderItemId, value) => {
    setQuantities((prev) => ({ ...prev, [orderItemId]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    const items = Object.entries(quantities)
      .map(([orderItemId, qty]) => ({
        orderItemId: parseInt(orderItemId),
        quantity: qty
      }))
      .filter((item) => typeof item.quantity === 'number' && item.quantity > 0);

    if (trackingNumber.trim().length === 0) {
      errorToast('Tracking number is required');
      setLoading(false);
      return;
    }

    if (items.length === 0) {
      errorToast('You must fulfill at least one item with a valid quantity');
      setLoading(false);
      return;
    }

    setSubmitting(true);
    try {
      await client.createFulfillment({
        orderId: order.id,
        trackingNumber,
        shippedAt: new Date().toISOString(),
        items
      });
      successToast('Fulfillment created and marked as shipped');
      refreshOrder?.();
      setTrackingNumber('');
    } catch (err) {
      console.error(err);
      errorToast('Error creating fulfillment');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (!order || !order.OrderItems) {
    return (
      <ContentContainer>
        <Subtitle>Loading order data...</Subtitle>
      </ContentContainer>
    );
  }

  if (allFulfilled) {
    return (
      <ContentContainer>
        <Subtitle>All items in this order have been fulfilled.</Subtitle>
      </ContentContainer>
    );
  }

  return (
    <MainContainer>
      {loading ?
        <Spinner />
      :
        <ContentContainer>
          <MainTitle>Fulfill Order #{order.refId}</MainTitle>

          <FulfillmentItemSelector
            orderItems={orderItemsWithRemaining}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            disabled={submitting}
          />
          
          <div style={{ marginTop: '10px' }}>
            <label>Tracking Number:</label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Tracking Number"
              disabled={submitting}
            />
          </div>

          <div style={{ marginTop: '20px' }}>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Create Fulfillment'}
            </Button>
          </div>
        </ContentContainer>
      }
    </MainContainer>
  );
};

export default AddFulfillment;